import React, { JSX, useCallback, useEffect, useMemo, useRef } from 'react';

import {
    ListRenderItemInfo,
    Modal,
    TouchableOpacity,
    View,
} from 'react-native';

import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

import styles from './styles';
import './styles.web.css';

type ListItem = {
    key: string;
    label: string;
    isSelected: boolean;
};

type Props<T extends ListItem> = {
    data: T[];
    isModalVisible: boolean;
    modalStartingPosition:
        | { top?: number; left?: number; bottom?: number; right?: number }
        | undefined;
    listHeight: number;
    listWidth: number;
    rowHeight: number;
    renderItem(item: ListRenderItemInfo<T>): JSX.Element;
    keyExtractor(item: T): string;
    onClose(): void;
};

function PickerList<T extends ListItem>({
    data,
    isModalVisible,
    modalStartingPosition = {},
    listHeight,
    listWidth,
    rowHeight,
    renderItem,
    keyExtractor,
    onClose,
}: Props<T>) {
    const itemsRefs = useRef<Record<ListItem['label'], View | null>>({});

    const handleModalShow = useCallback(() => {
        const selectedItemIndex = data.findIndex(
            ({ isSelected }) => isSelected,
        );
        const selectedItem =
            selectedItemIndex >= 0 ? data[selectedItemIndex] : null;
        const isOutOfBounds = (selectedItemIndex + 1) * rowHeight > listHeight;

        if (
            selectedItem &&
            isOutOfBounds &&
            itemsRefs.current[selectedItem.label]
        ) {
            // @ts-ignore
            itemsRefs.current[selectedItem.label].scrollIntoView();
        }
    }, [data, rowHeight, listHeight]);

    const dynamicStyles = useMemo(
        () => ({
            width: listWidth,
            ...modalStartingPosition,
        }),
        [listWidth, modalStartingPosition],
    );

    useEffect(() => {
        if (data.length) {
            const simpleBarContainers = document.querySelectorAll(
                'div[class^="simplebar"] ',
            );

            simpleBarContainers?.forEach((simpleBarContainer) => {
                simpleBarContainer.setAttribute('aria-label', ' ');
            });
        }
    }, [data]);

    return (
        <Modal
            visible={isModalVisible && modalStartingPosition !== undefined}
            transparent
            onRequestClose={onClose}
            animationType="none"
            onShow={handleModalShow}
            aria-modal
        >
            {isModalVisible && modalStartingPosition !== undefined && (
                <TouchableOpacity
                    style={styles.modalContainer}
                    activeOpacity={1}
                    onPressOut={onClose}
                >
                    <View style={[styles.container, dynamicStyles]}>
                        <SimpleBar style={{ height: listHeight }}>
                            {data.map((item) => (
                                <View
                                    key={keyExtractor(item)}
                                    ref={(datePickerItemRef) => {
                                        itemsRefs.current[item.label] =
                                            datePickerItemRef;
                                    }}
                                >
                                    {/*
                                // @ts-ignore */}
                                    {renderItem({ item })}
                                </View>
                            ))}
                        </SimpleBar>
                    </View>
                </TouchableOpacity>
            )}
        </Modal>
    );
}

export default PickerList;
